import Radio from '/components/elements/form/Radio'
import React, { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import isEmpty from 'lodash-es/isEmpty'
import capitalize from 'lodash-es/capitalize'
import toLower from 'lodash-es/toLower'
import lowerCase from 'lodash-es/lowerCase'
import { useSearchContext } from '/providers/SearchContext'
import axios from '/components/configs/axios'
import styles from './styles.module.scss'
import Grid from '/components/elements/Grid'
import classNames from 'classnames'
import CustomField from '/components/elements/form/CustomField'
import { translate } from '/components/helpers/translate'
import { useRouter } from 'next/router'

const HomeType = memo(
  ({
    form: { setFieldValue },
    target = 'vacation-rentals',
    shadowRef,
    setFiltersActive,
    translations,
    ...props
  }) => {
    const router = useRouter()
    const { search } = useSearchContext()
    const [additionalFields, setAdditionalFields] = useState([])
    const { isSmallMobile, isMobile } = useResponsive()

    const wrapperRef = useRef(null)
    const [show, setShow] = useState(false)
    const [checked, setChecked] = useState(search?.homeType || [])
    const [amenitiesChecked, setAmenitiesChecked] = useState(
      search?.amenities || []
    )
    const [dataLoaded, setDataLoaded] = useState(false)

    // Initial types with translations
    const [types, setTypes] = useState([
      {
        value: 'exclusive',
        label: translations.exclusiveproperties[router.locale]
      },
      { value: 'villa', label: translations.villas[router.locale] },
      {
        value: 'condominium',
        label: translations.condos[router.locale]
      }
    ])

    // String utility for replacing hyphens with spaces
    const replaceInArray = useCallback((str) => {
      return str && str.replace(/-/g, ' ')
    }, [])

    const [label, setLabel] = useState([...search.homeType])

    // Handle click outside
    const handleClickOutside = useCallback((event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false)
      }
    }, [])

    // Set up click outside handler
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [handleClickOutside])

    // Submit changes to parent form and context

    useEffect(() => {
      setFieldValue('homeType', [...checked])
      setFieldValue('amenities', [...amenitiesChecked])
      if (!isEmpty(checked) || !isEmpty(amenitiesChecked)) {
        setLabel([...checked, amenitiesChecked.map(replaceInArray).join(', ')])
      }
    }, [amenitiesChecked, checked])

    // Handle home type checkbox changes
    const handleChange = useCallback((e) => {
      setChecked((prevChecked) => {
        let updatedList = [...prevChecked]
        if (e.target.checked) {
          updatedList = [...prevChecked, e.target.value]
        } else {
          updatedList.splice(prevChecked.indexOf(e.target.value), 1)
        }
        return updatedList
      })
    }, [])

    // Handle amenities checkbox changes
    const handleAmenitiesChange = useCallback((e) => {
      setAmenitiesChecked((prevChecked) => {
        let updatedList = [...prevChecked]
        if (e.target.checked) {
          updatedList = [...prevChecked, e.target.value]
        } else {
          updatedList.splice(prevChecked.indexOf(e.target.value), 1)
        }
        return updatedList
      })
    }, [])

    // Format label text
    const labelFunction = useCallback(async () => {
      if (router.locale !== 'en' && typeof window !== 'undefined') {
        if (!isEmpty(search.amenities)) {
          setLabel([
            ...search.homeType,
            search.amenities.map(replaceInArray).join(', ')
          ])
        } else {
          setLabel([
            ...search.homeType,
            search.amenities.map(replaceInArray).join(', ')
          ])
        }
      }

      if (isEmpty(search.amenities) && !isEmpty(search.homeType)) {
        if (router.locale !== 'en') {
          setLabel(await translate(search.homeType))
        } else {
          setLabel(search.homeType)
        }
      }

      if (isEmpty(search.amenities) && isEmpty(search.homeType)) {
        setLabel(translations.filters[router.locale])
      }
    }, [
      search.amenities,
      search.homeType,
      router.locale,
      replaceInArray,
      translations
    ])

    // Sync with search context
    useEffect(() => {
      labelFunction()
      setAmenitiesChecked(search?.amenities || [])
      setChecked(search?.homeType || [])
    }, [search.amenities, search.homeType, labelFunction])

    // Load property types for real estate
    useEffect(() => {
      if (target !== 'vacation-rentals') {
        axios.get('real-estate/property-type').then((res) => {
          const realEstateTypes = res.data[router.locale].map((type) => {
            return { value: lowerCase(type).replace(' ', '-'), label: type }
          })
          setTypes([
            {
              value: 'exclusive',
              label: translations.exclusiveproperties[router.locale]
            },
            ...realEstateTypes
          ])
        })
      }
    }, [router.locale, target, translations])

    // Load amenities
    useEffect(() => {
      const endpoint =
        target === 'vacation-rentals'
          ? 'amenities/tag'
          : 'real-estate/popular-amenities'

      axios
        .get(endpoint)
        .then((res) => {
          if (target === 'vacation-rentals') {
            setAdditionalFields(res.data[router.locale])
          } else {
            setAdditionalFields({ amenities: res.data[router.locale] })
          }
          setDataLoaded(true)
        })
        .catch((err) => console.error(err))

      setAmenitiesChecked(search?.amenities || [])
    }, [router.locale, target, search?.amenities])

    // Submit when checkbox selections change
    // useEffect(() => {
    //   handleSubmit()
    // }, [checked, amenitiesChecked, handleSubmit])

    // Handle shadow overlay
    useEffect(() => {
      if (shadowRef) {
        if (show) {
          shadowRef.current?.classList.add('isVisible')
        } else {
          shadowRef.current?.classList.remove('isVisible')
        }
      }

      if (setFiltersActive) {
        setFiltersActive(
          show || !isEmpty(search.amenities) || !isEmpty(search.homeType)
        )
      }
    }, [shadowRef, show, search.amenities, search.homeType, setFiltersActive])

    // Toggle show/hide
    const toggleShow = useCallback(() => {
      setShow((prev) => !prev)
    }, [])

    // Track if filters content should be in DOM with a separate state
    const [filtersMounted, setFiltersMounted] = useState(false)

    // Use effect to handle mounting/unmounting with proper timing for transitions
    useEffect(() => {
      if (show) {
        // When opening, mount content immediately
        setFiltersMounted(true)
      } else if (!show && filtersMounted) {
        // When closing, wait for transition to finish before unmounting
        const transitionDuration = 300 // Match this with your CSS transition duration
        const timer = setTimeout(() => {
          setFiltersMounted(false)
        }, transitionDuration)

        return () => clearTimeout(timer)
      }
    }, [show, filtersMounted])

    return (
      <div
        ref={wrapperRef}
        className={styles.field}
        role="group"
        aria-labelledby="bedrooms-group"
      >
        <CustomField
          aria-label="Select Home Type"
          type="text"
          readOnly
          angle
          variant={[isMobile && 'full-width']}
          onClick={toggleShow}
          value={label}
          isActive={!isEmpty(checked) || !isEmpty(amenitiesChecked)}
          {...props}
        />

        <div
          className={classNames(styles.field__radios, show && styles.isShown)}
        >
          {filtersMounted && dataLoaded && (
            <div className={styles.filters}>
              <div>
                <span className={styles.filters__label}>
                  {translations.homeType[router.locale]}
                </span>

                {types.map((item, index) => (
                  <Radio
                    key={`type-${index}`}
                    name="homeType"
                    checkbox
                    value={item.value}
                    label={item.label}
                    checked={checked?.includes(item.value)}
                    onChange={handleChange}
                    disabled={item.disabled}
                  />
                ))}
              </div>

              {!isEmpty(additionalFields.amenities) && (
                <div className={styles.filters__amenities}>
                  <span className={styles.filters__label}>
                    {translations.amenities[router.locale]}
                  </span>

                  <Grid
                    variant={[isSmallMobile ? 'grid-2' : 'rows-5', 'rows-2xs']}
                  >
                    {additionalFields.amenities.map((item, index) => (
                      <Radio
                        key={`amenity-${index}`}
                        variant={['is-close', 'small']}
                        name="homeType"
                        checkbox
                        value={toLower(item)}
                        label={capitalize(item)}
                        checked={amenitiesChecked?.includes(toLower(item))}
                        onChange={handleAmenitiesChange}
                        disabled={item.disabled}
                      />
                    ))}
                  </Grid>
                </div>
              )}

              {!isEmpty(additionalFields.offers) && (
                <div className={styles.filters__amenities}>
                  <span className={styles.filters__label}>Promos</span>

                  <Grid
                    variant={[isSmallMobile ? 'grid-2' : 'rows-5', 'rows-2xs']}
                  >
                    {additionalFields.offers.map((item, index) => (
                      <Radio
                        key={`amenity-${index}`}
                        variant={['is-close', 'small']}
                        name="homeType"
                        checkbox
                        value={toLower(item)}
                        label={capitalize(item)}
                        checked={amenitiesChecked?.includes(toLower(item))}
                        onChange={handleAmenitiesChange}
                        disabled={item.disabled}
                      />
                    ))}
                  </Grid>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)

// Display name for debugging
HomeType.displayName = 'HomeType'

export default HomeType
